import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { resetPassword } from '../services/authService';
import Navbar from '../components/Navbar';
import './Login.css';

const ResetPassword = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Get email and OTP from location state or localStorage
    const emailFromState = location.state?.email;
    const otpFromState = location.state?.otp;
    const emailFromStorage = localStorage.getItem('resetEmail');

    if (emailFromState && otpFromState) {
      setEmail(emailFromState);
      setOtp(otpFromState);
    } else if (emailFromStorage) {
      setEmail(emailFromStorage);
      // If we have email but no OTP, redirect to verify OTP page
      if (!otpFromState) {
        navigate('/verify-reset-otp');
      }
    } else {
      // No email found, redirect to forgot password page
      navigate('/forgot-password');
    }
  }, [location, navigate]);

  const getPasswordStrength = () => {
    if (!newPassword) return { level: '', text: '' };
    if (newPassword.length < 6) return { level: 'weak', text: 'Weak' };
    if (newPassword.length < 10) return { level: 'medium', text: 'Medium' };
    if (/[A-Z]/.test(newPassword) && /[0-9]/.test(newPassword) && /[^A-Za-z0-9]/.test(newPassword)) {
      return { level: 'strong', text: 'Strong' };
    }
    return { level: 'medium', text: 'Medium' };
  };

  const passwordStrength = getPasswordStrength();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password length
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);

    try {
      const response = await resetPassword(email, otp, newPassword);
      setSuccess(response.message || 'Password reset successfully');

      // Clear stored email and redirect to login after 2 seconds
      setTimeout(() => {
        localStorage.removeItem('resetEmail');
        navigate('/login');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <Navbar />

      <div className="login-wrapper">
        <div className="login-card">
          {/* Decorative elements */}
          <div className="login-decor login-decor-1"></div>
          <div className="login-decor login-decor-2"></div>

          <div className="login-header">
            <div className="login-icon">
              <i className="bi bi-lock-fill"></i>
            </div>
            <h2>Reset Password</h2>
            <p>Enter your new password</p>
          </div>

          <form onSubmit={handleSubmit} className="login-form">
            {error && (
              <div className="login-alert login-alert-error">
                <i className="bi bi-exclamation-circle"></i>
                <span>{error}</span>
              </div>
            )}

            {success && (
              <div className="login-alert login-alert-info">
                <i className="bi bi-check-circle"></i>
                <span>{success}</span>
              </div>
            )}

            <div className="login-field">
              <label>
                <i className="bi bi-envelope"></i>
                Email
              </label>
              <div className="login-input-wrapper">
                <input
                  type="email"
                  value={email}
                  disabled
                  style={{ opacity: 0.7 }}
                />
              </div>
            </div>

            <div className="login-field">
              <label htmlFor="newPassword">
                <i className="bi bi-lock"></i>
                New Password
              </label>
              <div className="login-input-wrapper">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  id="newPassword"
                  placeholder="Enter new password (min 6 characters)"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  disabled={loading || !!success}
                  minLength={6}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="login-password-toggle"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                >
                  <i className={`bi ${showNewPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                </button>
              </div>
            </div>

            {newPassword && (
              <div className="password-strength" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                <div style={{
                  flex: 1,
                  height: '4px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '2px',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    height: '100%',
                    borderRadius: '2px',
                    transition: 'all 0.3s ease',
                    width: passwordStrength.level === 'weak' ? '33%' : passwordStrength.level === 'medium' ? '66%' : '100%',
                    background: passwordStrength.level === 'weak' ? '#ff6b6b' : passwordStrength.level === 'medium' ? '#ffc107' : '#BFFF00'
                  }}></div>
                </div>
                <span style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                  {passwordStrength.text}
                </span>
              </div>
            )}

            <div className="login-field">
              <label htmlFor="confirmPassword">
                <i className="bi bi-lock-fill"></i>
                Confirm New Password
              </label>
              <div className="login-input-wrapper">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  placeholder="Re-enter new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={loading || !!success}
                  minLength={6}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="login-password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  <i className={`bi ${showConfirmPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                </button>
              </div>
            </div>

            {confirmPassword && newPassword && (
              <div style={{
                marginBottom: '16px',
                fontSize: '0.85rem',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                {newPassword === confirmPassword ? (
                  <>
                    <i className="bi bi-check-circle" style={{ color: '#BFFF00' }}></i>
                    <span style={{ color: '#BFFF00' }}>Passwords match</span>
                  </>
                ) : (
                  <>
                    <i className="bi bi-x-circle" style={{ color: '#ff6b6b' }}></i>
                    <span style={{ color: '#ff6b6b' }}>Passwords do not match</span>
                  </>
                )}
              </div>
            )}

            <button
              type="submit"
              className="login-btn"
              disabled={loading || !!success}
            >
              {loading ? (
                <>
                  <span className="login-spinner"></span>
                  Resetting Password...
                </>
              ) : (
                <>
                  Reset Password
                  <i className="bi bi-arrow-right"></i>
                </>
              )}
            </button>
          </form>

          <div className="login-divider">
            <span>or</span>
          </div>

          <div className="login-footer">
            <p>
              Remember your password? <Link to="/login">Back to Login</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
